















import { Component, Prop, Vue } from 'vue-property-decorator';
import { GraphsApi } from '@/api';
import moment from 'moment';
import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import {
	Bullet,
	Circle,
	Color,
	Label,
	Legend,
	percent,
	Root,
	Scrollbar,
	Tooltip,
} from '@amcharts/amcharts5';
import { DateAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/DateAxis';
import { AxisRenderer } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import { ApiUtils } from '@/common/ApiUtils';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { filterArrayByTsAfter2010 } from '@/common/DateFilterUtil';

@Component({
	components: { DatePicker },
})
export default class EntityChargingBehaviourChart extends Vue {
	@Prop({ required: true })
	public entityId!: number;

	@Prop({ required: true })
	public entityType!: string;

	public datePicker?: DatePicker | null = null;

	public errorMessage: string | null = null;
	public loading: boolean = true;
	public graphData: any[] | null = null;

	private graphApi: GraphsApi = new GraphsApi();

	private solarPowerSeries: LineSeries | null = null;
	private solarVoltageSeries: LineSeries | null = null;
	private chargeStateSeries: LineSeries | null = null;

	private xAxis: DateAxis<AxisRenderer> | null = null;
	private solarPowerYAxis: ValueAxis<AxisRenderer> | null = null;
	private solarVoltageYAxis: ValueAxis<AxisRenderer> | null = null;

	private startDate: moment.Moment = moment()
		.startOf('day')
		.subtract(5, 'days');
	private endDate: moment.Moment = moment()
		.startOf('day')
		.add(1, 'days');

	public async created(): Promise<void> {
		if (this.entityId == null || this.entityType == null) {
			this.errorMessage = 'Invalid entity provided';
			return;
		}
		await this.fetchGraphData(this.entityId, this.entityType);
		this.createChart(this.graphData!);

		this.datePicker = <DatePicker>this.$refs.datePicker;
		this.datePicker?.onDateRangeChanged(<DateRange>{
			start: this.startDate.format('DD-MM-YYYY'),
			end: this.endDate.format('DD-MM-YYYY'),
		});
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		this.startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		this.endDate = moment(newDateRange.end, 'DD-MM-YYYY');

		await this.fetchGraphData(this.entityId, this.entityType);

		this.solarPowerSeries?.data.setAll(this.formatGraphData(this.graphData!, 'solar_mW'));
		this.solarVoltageSeries?.data.setAll(this.formatGraphData(this.graphData!, 'solar_mV'));
		this.chargeStateSeries?.data.setAll(
			this.formatGraphData(this.graphData!, 'solar_mW', 'chrg_state'),
		);

		this.setSolarYAxisRanges();

		this.xAxis?.setAll({
			min: this.startDate.valueOf(),
			max: Math.min(this.endDate.valueOf(), Date.now()),
		});
	}

	public getRangeMax(variable: string): number {
		return (
			Math.max(...(this.graphData?.map(x => x[variable] ?? null)?.filter(x => x != null) ?? [])) *
			1.1
		);
	}

	public setSolarYAxisRanges() {
		this.solarPowerYAxis?.setAll({
			min: 0,
			max: this.getRangeMax('solar_mW'),
		});
		this.solarVoltageYAxis?.setAll({
			min: 0,
			max: this.getRangeMax('solar_mV'),
		});
	}

	private async fetchGraphData(entityId: number, entityType: string): Promise<void> {
		try {
			const graphDataResponse = await this.graphApi.graph(
				entityType,
				entityId,
				this.startDate.format('X'),
				this.endDate.format('X'),
				'power',
			);
			this.graphData =
				(graphDataResponse.data.data?.power ?? []).map((x: any) => {
					const chrg_state = x?.value?.chrg_state;
					switch (chrg_state) {
						case 'c/x_term':
							x.value.chrg_state = 'Full';
							break;
						case 'cc_cv_chrg':
							x.value.chrg_state = 'Charging';
							break;
						case 'chrg_susp':
							x.value.chrg_state = 'No Solar';
							break;
					}
					return { ...x.value, timestamp: x.timestamp };
				}) ?? [];

			this.graphData = filterArrayByTsAfter2010(this.graphData);

			if (!this.graphData?.some((x: any) => x.solar_mV > -1000 || x.solar_mW > -1000)) {
				this.graphData = [];
			}
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
			if (this.errorMessage === 'Resource not found') this.errorMessage = null;
			this.graphData = [];
		} finally {
			this.loading = false;
		}
	}

	private createChart(graphData: any[]): void {
		const root: Root = Root.new('chargingBehaviorChart');
		const chart: XYChart = XYChart.new(root, {});

		const {
			cursor,
			xAxis,
			solarPowerYAxis,
			solarVoltageYAxis,
			solarPowerSeries,
			solarVoltageSeries,
			chargeStateSeries,
			legend,
		} = this.createChartFeatures(root);
		this.solarPowerSeries = solarPowerSeries;
		this.solarVoltageSeries = solarVoltageSeries;
		this.chargeStateSeries = chargeStateSeries;

		this.solarPowerYAxis = solarPowerYAxis;
		this.solarVoltageYAxis = solarVoltageYAxis;

		this.setSolarYAxisRanges();

		this.xAxis = xAxis;
		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);

		chart.yAxes.push(solarPowerYAxis);
		chart.yAxes.push(solarVoltageYAxis);

		chart.series.push(solarPowerSeries);
		chart.series.push(solarVoltageSeries);
		chart.series.push(chargeStateSeries);

		let scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);

		solarPowerSeries.data.setAll(this.formatGraphData(graphData, 'solar_mW'));
		solarVoltageSeries.data.setAll(this.formatGraphData(graphData, 'solar_mV'));
		chargeStateSeries.data.setAll(this.formatGraphData(graphData, 'solar_mW', 'chrg_state'));

		legend.data.setAll(chart.series.values);
		root.container.children.push(chart);

		solarPowerSeries.appear(1000);
		solarVoltageSeries.appear(1000);
		chargeStateSeries.appear(1000);

		chart.appear(1000, 100);
	}

	private formatGraphData(
		graphData: any[],
		field: string,
		labelField?: string,
		min?: number,
	): {
		value: number | undefined;
		date: number;
	}[] {
		return (
			graphData
				?.filter((item: any) => min == null || item[field] >= min)
				.map((item: any, index: number) => {
					let out: any = {
						value: Math.max(0, item[field]),
						date: item.timestamp,
					};

					if (labelField) {
						const diffToPrev = index === 0 || graphData[index - 1][labelField] !== item[labelField];
						if (diffToPrev) out.label = item[labelField];
						else return null;
					}
					return out;
				})
				?.filter(x => x != null) ?? []
		);
	}

	private createChartFeatures(
		root: Root,
	): {
		xAxis: DateAxis<AxisRenderer>;
		solarPowerYAxis: ValueAxis<AxisRenderer>;
		solarVoltageYAxis: ValueAxis<AxisRenderer>;
		cursor: XYCursor;
		solarPowerSeries: LineSeries;
		solarVoltageSeries: LineSeries;
		chargeStateSeries: LineSeries;
		legend: Legend;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: this.startDate.toDate().getTime(),
			max: Math.min(this.endDate.toDate().getTime(), Date.now()),
		});

		const solarPowerYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
		});

		const solarVoltageYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
		});

		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });

		const solarPowerSeries: LineSeries = LineSeries.new(root, {
			name: 'Solar Power (mW)',
			xAxis,
			yAxis: solarPowerYAxis,
			tooltip: Tooltip.new(root, {
				labelText: '{valueY}mW',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0xf6861f),
			stroke: Color.fromHex(0xf6861f),
		});
		solarPowerSeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const solarVoltageSeries: LineSeries = LineSeries.new(root, {
			name: 'Solar Voltage (mV)',
			xAxis,
			yAxis: solarVoltageYAxis,
			tooltip: Tooltip.new(root, {
				labelText: '{valueY}mV',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0x769edf),
			stroke: Color.fromHex(0x769edf),
		});
		solarVoltageSeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const chargeStateSeries = LineSeries.new(root, {
			name: 'Charge State',
			xAxis,
			yAxis: solarPowerYAxis,
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			fill: Color.fromHex(0xf6861f),
			stroke: Color.fromHex(0xf6861f),
		});

		chargeStateSeries.bullets.push(r => {
			return Bullet.new(r, {
				locationX: 0.5,
				locationY: 0.5,
				sprite: Circle.new(r, {
					radius: 3,
					fill: Color.fromRGB(255, 255, 255),
					stroke: Color.fromRGB(170, 170, 170),
					strokeWidth: 2,
				}),
			});
		});
		chargeStateSeries.bullets.push(r => {
			return Bullet.new(r, {
				sprite: Label.new(r, {
					text: '{label}',
					centerX: percent(50),
					centerY: percent(100),
					populateText: true,
					fontFamily: 'sofia-pro-soft',
					fontSize: 11,
					fontWeight: '400',
				}),
			});
		});

		const solarPowerYAxisRenderer = solarPowerYAxis.get('renderer');
		solarPowerYAxisRenderer.grid.template.setAll({
			forceHidden: true,
		});
		solarPowerYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: solarPowerSeries.get('fill'),
		});

		const solarVoltageYAxisRenderer = solarVoltageYAxis.get('renderer');
		solarVoltageYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: solarVoltageSeries.get('fill'),
		});

		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		return {
			xAxis,
			solarPowerYAxis,
			solarVoltageYAxis,
			cursor,
			solarPowerSeries,
			solarVoltageSeries,
			chargeStateSeries,
			legend,
		};
	}
}
